import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from './AuthContext';
interface ProgressContextType {
  saveProgress: (step: string, courseId?: string, completedModules?: string[]) => Promise<void>;
  loadProgress: () => Promise<string | null>;
  currentStep: string | null;
  isLoading: boolean;
}
const ProgressContext = createContext<ProgressContextType | undefined>(undefined);
export const ProgressProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    user
  } = useAuth();
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Load progress when user changes
  useEffect(() => {
    if (user) {
      loadProgressData().then(step => {
        setCurrentStep(step);
        setIsLoading(false);
      });
    } else {
      setCurrentStep(null);
      setIsLoading(false);
    }
  }, [user]);
  // Save progress function
  const saveProgress = async (step: string, courseId?: string, completedModules?: string[]) => {
    if (!user) {
      console.error('Not logged in');
      return;
    }
    try {
      setIsLoading(true);
      const upsertData: any = {
        user_id: user.id,
        current_step: step,
        updated_at: new Date().toISOString()
      };
      // Add optional fields if provided
      if (courseId) upsertData.course_id = courseId;
      if (completedModules) upsertData.completed_modules = completedModules;
      const {
        error
      } = await supabase.from('progress').upsert(upsertData, {
        onConflict: courseId ? 'user_id, course_id' : 'user_id'
      });
      if (error) {
        console.error('Failed to save progress:', error.message);
      } else {
        console.log('Progress saved:', step);
        setCurrentStep(step);
      }
    } finally {
      setIsLoading(false);
    }
  };
  // Load progress function
  const loadProgressData = async (): Promise<string | null> => {
    if (!user) {
      console.error('Not logged in');
      return null;
    }
    try {
      const {
        data,
        error
      } = await supabase.from('progress').select('current_step').eq('user_id', user.id).single();
      if (error && error.code !== 'PGRST116') {
        console.error('Failed to load progress:', error.message);
        return null;
      }
      return data?.current_step || null;
    } catch (error) {
      console.error('Error loading progress:', error);
      return null;
    }
  };
  // Public loadProgress function (returns promise)
  const loadProgress = async () => {
    if (currentStep) return currentStep;
    return loadProgressData();
  };
  return <ProgressContext.Provider value={{
    saveProgress,
    loadProgress,
    currentStep,
    isLoading
  }} data-id="element-314">
      {children}
    </ProgressContext.Provider>;
};
// Custom hook to use the progress context
export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (context === undefined) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};